// Won and lost (Iris's screen 7): win rate by source, all time, with one row of filters (period, source, contact type,
// job size from the job_size_bands setting) and a choice of what the rows are. Lost splits before and after a proposal
// (a done step from the Proposal event on).

import { useState } from 'react';
import { sizeBand, winRows, type ClosedLead } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { money } from '../../lib/format';
import { usePipeline, type Lead } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Loading, Panel, Seg } from '../../ui/kit';

type Period = 'all' | '12m' | 'year' | 'dates';
type By = 'source' | 'type' | 'size' | 'year';

export function WonLostPage() {
  const p = usePipeline();
  const { can } = useSession();
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline', to: '/pipeline' }, { label: 'Won and lost' }]);
  const [period, setPeriod] = useState<Period>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState('');
  const [type, setType] = useState('');
  const [size, setSize] = useState('');
  const [by, setBy] = useState<By>('source');
  if (p.loading) return <Loading what="Loading won and lost" />;
  const prices = can('see_prices');

  // a retired record counts nowhere (decision 1, 2026-10-19)
  const closed = p.leads.filter((l) => (l.outcome === 'won' || l.outcome === 'lost') && !l.retired);
  const closedAt = (l: Lead) => String(l.opp.outcome_at ?? l.opp.created_at).slice(0, 10);
  const now = new Date();
  const since12 = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString().slice(0, 10);
  const yearStart = `${now.getFullYear()}-01-01`;
  const inPeriod = (l: Lead) => {
    const d = closedAt(l);
    if (period === '12m') return d >= since12;
    if (period === 'year') return d >= yearStart;
    if (period === 'dates') return (!from || d >= from) && (!to || d <= to);
    return true;
  };
  const sourceOf = (l: Lead) => String(l.opp.source ?? '').trim() || 'Source not set';
  const typeOf = (l: Lead) => String(l.primary?.type ?? '').trim() || 'No contact type';
  const sizeOf = (l: Lead) => sizeBand(l.value.cents, p.bands);
  const yearOf = (l: Lead) => closedAt(l).slice(0, 4);
  const shown = closed.filter((l) => inPeriod(l) && (!source || sourceOf(l) === source) && (!type || typeOf(l) === type) && (!size || sizeOf(l) === size));
  const groupOf = by === 'source' ? sourceOf : by === 'type' ? typeOf : by === 'size' ? sizeOf : yearOf;
  const rows = winRows(shown.map((l): ClosedLead => ({ outcome: l.outcome as 'won' | 'lost', group: groupOf(l), cents: l.value.cents ?? 0, afterProposal: l.afterProposal })));
  const most = Math.max(1, ...rows.filter((r) => r.group !== '').map((r) => Math.max(r.won, r.lostBefore + r.lostAfter)));
  const bar = (n: number) => `${(n / most) * 100}%`;
  const total = rows.find((r) => r.group === '');
  const options = (f: (l: Lead) => string) => [...new Set(closed.map(f))].sort();

  return (
    <div className="stack">
      <div className="head">
        <div>
          <h1>Won and lost</h1>
          <small>Win rate by source, contact type, job size or year. All closed opportunities, Notion's history included.</small>
        </div>
      </div>
      <Panel className="pwl-panel">
        <div className="pfilters">
          <Seg items={[{ key: 'all', label: 'All time' }, { key: '12m', label: '12 months' }, { key: 'year', label: 'This year' }, { key: 'dates', label: 'Dates' }]} value={period} onChange={setPeriod} />
          {period === 'dates' && (
            <span className="pinline">
              <input type="date" className="read" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
              <span>to</span>
              <input type="date" className="read" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
            </span>
          )}
          <select className="read" value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
            <option value="">Source · all</option>
            {options(sourceOf).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <select className="read" value={type} onChange={(e) => setType(e.target.value)} aria-label="Contact type">
            <option value="">Contact type · all</option>
            {options(typeOf).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <select className="read" value={size} onChange={(e) => setSize(e.target.value)} aria-label="Job size">
            <option value="">Job size · all</option>
            {options(sizeOf).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <span className="pby">
            <span className="lb">Rows</span>
            <Seg items={[{ key: 'source', label: 'Source' }, { key: 'type', label: 'Contact type' }, { key: 'size', label: 'Job size' }, { key: 'year', label: 'Year' }]} value={by} onChange={setBy} />
          </span>
        </div>
        <small>
          {shown.length} closed · {period === 'all' ? 'all time' : period === '12m' ? 'last 12 months' : period === 'year' ? 'this year' : 'the dates picked'}
        </small>
        <div className="pwl">
          <span className="pwl-h">{by === 'source' ? 'Source' : by === 'type' ? 'Contact type' : by === 'size' ? 'Job size' : 'Year'}</span>
          <span className="pwl-h pwl-mid">
            <span>
              Lost · <i className="psw lb-sw" /> before <i className="psw" /> after proposal
            </span>
            <span>Won</span>
          </span>
          <span className="pwl-h right">Win rate</span>
          {prices && <span className="pwl-h right">Won $</span>}
          {rows
            .filter((r) => r.group !== '')
            .map((r) => (
              <RowCells key={r.group} label={r.group} won={r.won} before={r.lostBefore} after={r.lostAfter} rate={r.ratePct} cents={r.wonCents} bar={bar} prices={prices} unset={r.group.endsWith('not set') || r.group.startsWith('No ')} />
            ))}
          {total && (
            <div className="pwl-row tot">
              <span className="pwl-tot pwl-name">All</span>
              <span className="pwl-tot pwl-mid small num" style={{ justifyContent: 'center' }}>
                {total.lostBefore + total.lostAfter} lost ({total.lostBefore} before · {total.lostAfter} after) · {total.won} won
              </span>
              <b className="pwl-tot right num">{total.ratePct === null ? '—' : `${total.ratePct}%`}</b>
              {prices && <span className="pwl-tot right num pwl-cents">{money(total.wonCents, { whole: true })}</span>}
            </div>
          )}
          {rows.length === 0 && <span style={{ gridColumn: '1 / -1' }} className="empty">Nothing closed in this view.</span>}
        </div>
        <small>Lost "after proposal" means the Proposal step, or a later one, was done on the lead. Leads brought over from Notion carry no steps, so they all read as before.</small>
      </Panel>
    </div>
  );
}

function RowCells({ label, won, before, after, rate, cents, bar, prices, unset }: { label: string; won: number; before: number; after: number; rate: number | null; cents: number; bar: (n: number) => string; prices: boolean; unset: boolean }) {
  return (
    <div className="pwl-row">
      <span className="pwl-name">{unset ? <span className="tag un">{label}</span> : label}</span>
      <span className="pdiv">
        <span className="pdiv-l">
          <span className="num">{before + after}</span>
          <i className="lb-sw" style={{ width: bar(before) }} />
          <i style={{ width: bar(after) }} />
        </span>
        <span className="pdiv-axis" />
        <span className="pdiv-w">
          <i style={{ width: bar(won) }} />
          <span className="num">{won}</span>
        </span>
      </span>
      <b className="right num pwl-rate">{rate === null ? '—' : `${rate}%`}</b>
      {prices && <span className="right num pwl-cents">{money(cents, { whole: true })}</span>}
    </div>
  );
}
